import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Typography,
  FormGroup,
  IconButton,
  Box,
} from "@mui/material";
import HelpIcon from "@mui/icons-material/Help";
import CloseIcon from "@mui/icons-material/Close";
import ChatIcon from "@mui/icons-material/Chat";
import { useCallback, useRef, useState } from "react";
import { Form } from "@shared/components/Form";
import { TextField } from "@shared/components/TextField";
import {
  SupportThreadFormSchema,
  supportThreadFormSchema,
  SUPPORT_THREAD_SCHEMA_DEFAULT_VALUES,
  SUPPORT_DESCRIPTION_MAX_LENGTH,
} from "../support-form-helpers";
import { useForm, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { SubmitButton } from "@avenue-8/sidekick-ui";
import { useSendThreadSupportMessage } from "@queries/chat/useSendThreadSupportMessage";
import { CharacterCounter } from "./CharacterCounter";
import { useUIStore } from "@store/ui.store";
import { SuccessAnimatedIcon } from "@shared/components/SuccessAnimatedIcon";
import { useIntercomIntegration } from "@shared/services/intercom/useIntercomIntegration";

type Props = {
  threadId: string;
};

export const ChatThreadSupport = (props: Props) => {
  const { threadId } = props;
  const [conversationId, setConversationId] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const supportDialogMode = useUIStore(
    (state) => state.supportDialogOptions.mode,
  );
  const toggleSupportDialogMode = useUIStore(
    (state) => state.toggleSupportDialogMode,
  );
  const { showSpace, showConversation } = useIntercomIntegration();
  const supportFormMethods = useForm<SupportThreadFormSchema>({
    defaultValues: {
      ...SUPPORT_THREAD_SCHEMA_DEFAULT_VALUES,
    },
    resolver: yupResolver(supportThreadFormSchema),
    reValidateMode: "onChange",
  });
  const {
    register,
    formState: { errors },
    handleSubmit,
    reset,
  } = supportFormMethods;

  const supportMessageMutation = useSendThreadSupportMessage({
    onSuccess: (data) => {
      reset();
      setConversationId(data.conversationId);
    },
  });

  const onSubmit = (data: SupportThreadFormSchema) => {
    supportMessageMutation.mutate({
      threadId,
      description: data.description,
      numberOfMessages: data.numberOfMessages,
    });
  };

  const resetSupportMessageMutation = supportMessageMutation.reset;

  const onClose = useCallback(() => {
    toggleSupportDialogMode({ mode: null });
    setTimeout(() => {
      resetSupportMessageMutation();
      setConversationId(null);
    }, 1000);
  }, [toggleSupportDialogMode, resetSupportMessageMutation]);

  const onOpenChat = useCallback(() => {
    onClose();
    if (conversationId) {
      showConversation(conversationId);
    } else {
      showSpace("home");
    }
  }, [onClose, showConversation, showSpace, conversationId]);

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      onClose={onClose}
      open={supportDialogMode === "support-request"}
    >
      <DialogTitle id="support-dialog-title">
        Support
        <IconButton
          sx={{ position: "absolute", right: "1rem", top: "1rem" }}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      {!supportMessageMutation.isSuccess && (
        <>
          <DialogContent id="support-dialog-content">
            <Form
              onSubmit={handleSubmit(onSubmit)}
              id="support-form"
              ref={formRef}
              aria-disabled={supportMessageMutation.isPending}
            >
              <Typography mb={2}>
                Please provide specific details about the issue you’re
                experiencing.
                <br />
                We’ll also gather data from your recent messages to assist you
                better.
              </Typography>
              <FormGroup sx={{ position: "relative", marginBottom: "1rem" }}>
                <TextField
                  label="Description"
                  name="description"
                  inputProps={{
                    ...register("description"),
                    maxLength: SUPPORT_DESCRIPTION_MAX_LENGTH,
                  }}
                  fieldError={errors.description}
                  multiline
                  maxRows={10}
                  minRows={1}
                  autoFocus
                />
                <FormProvider {...supportFormMethods}>
                  <CharacterCounter
                    maxLength={SUPPORT_DESCRIPTION_MAX_LENGTH}
                    bottom="1.5rem"
                    right="0.75rem"
                  />
                </FormProvider>
              </FormGroup>
            </Form>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={onClose}
              variant="text"
              disabled={supportMessageMutation.isPending}
            >
              Cancel
            </Button>

            <SubmitButton
              type="submit"
              form="support-form"
              isLoading={supportMessageMutation.isPending}
            >
              Submit
            </SubmitButton>
          </DialogActions>
        </>
      )}
      {supportMessageMutation.isSuccess && (
        <>
          <DialogContent
            id="support-dialog-content-success"
            sx={{ padding: "3rem" }}
          >
            <SuccessAnimatedIcon />

            <Box textAlign="center">
              <Typography mb={2}>
                Thanks for reaching out—we’re here to help!
              </Typography>
              <Typography>
                We’ve received your message and will follow up with you soon.
              </Typography>
              <Typography mb={3}>
                To ensure you don’t miss anything, we’ll respond both in the app
                and via email.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={<ChatIcon />}
                onClick={onOpenChat}
              >
                View Conversation
              </Button>
              <Typography variant="body2" mt={3}>
                You can easily revisit this conversation by clicking the{" "}
                <HelpIcon fontSize="small" sx={{ verticalAlign: "middle" }} />{" "}
                button in the bottom-right corner.
              </Typography>
            </Box>
          </DialogContent>
        </>
      )}
    </Dialog>
  );
};
